import { isMultiMarketRole, permissions, UserRole } from '@utilities-one/reipm-api-sdk';

import type { AccessContextType } from '@/entities/user/providers/accessContext';
import type { AccessUser } from '@/entities/user/types';

/**
 * Whether the signed-in user may edit the multi-market block of a user holding
 * `targetRoleId` — the API's rule: any role but System Admin, a
 * `users.multiMarket.manage` holder, and only a System Admin for an Admin target.
 */
export const canManageMultiMarketFor = (
  access: Pick<AccessContextType, 'user' | 'hasPermission'>,
  targetRoleId: string
): boolean =>
  isMultiMarketRole(targetRoleId) &&
  access.hasPermission(permissions.users.multiMarket.manage) &&
  (targetRoleId !== UserRole.admin || access.user.role.id === UserRole.systemAdmin);

/**
 * The markets the edited user's clients may come from, as the form currently
 * reads them: the single Market while the multi-market block is hidden, every
 * listed market with access on, else the first listed market (the API's snap).
 */
export const allowedMarketIdsOf = (
  values: { marketId: string; multiMarketAccess: boolean; availableMarketIds: string[] },
  multiBlock: boolean
): string[] => {
  if (!multiBlock) return values.marketId ? [values.marketId] : [];
  if (values.multiMarketAccess) return values.availableMarketIds;
  return values.availableMarketIds.slice(0, 1);
};

/**
 * Whether the user gets the post-login market picker / topbar switcher:
 * multi-market access with at least two listed markets. Structurally typed so
 * both the `me` payload and the login response fit.
 */
export const canSwitchMarkets = (user: Pick<AccessUser, 'multiMarketAccess' | 'availableMarkets'>): boolean =>
  user.multiMarketAccess && user.availableMarkets.length > 1;

/**
 * The markets a user belongs to: the curated multi-market list while
 * multi-market access is on, else the single active market. The canonical
 * resolver shared by the users grid's Market cell, the `/overall` info card,
 * and the edit form's multi-market seeding so all three resolve the concept
 * identically.
 */
export const marketsOf = <T extends { id: string; name: string }>(user: {
  multiMarketAccess: boolean;
  availableMarkets: T[];
  market: T | null | undefined;
}): T[] => {
  if (user.multiMarketAccess && user.availableMarkets.length > 0) return user.availableMarkets;
  return user.market ? [user.market] : [];
};
